import * as React from "react";
import { Card } from "../../card";
import { StatCard } from "../stat-card";
import { DataTable, type DataTableColumn } from "../data-table";
import { SectionTitle } from "../section-title";
import { StatusBadge, IntentBadge } from "../status-badge";
import { formatRelativeTime } from "../../format";
import { useApi } from "../../api/client";
import { intake as intakeContract } from "../../api/contracts/intake";
import type { IntakeBatchLedgerRow, IntakeSource } from "../../api/contracts/intake";
import type { Intent } from "../../tokens";

/**
 * /database Intake ledger card (INTAKE-PRD §2.8.2 / slice U-A). The trust
 * surface for Universal Intake: recent batches + a roll-up stat strip. Appended
 * below RetentionHealthCard on `/database`, mirroring its grammar (Card p-6,
 * plain SectionTitle + count, StatCard strip, DataTable wrapInCard={false}).
 * Read-only ledger foundation — per-batch drill + server-real Undo land in U-B.
 * All data flows through the zod contract + createRoute (no raw fetch).
 */

/** Source → intent for the ingress-mode pill. Semantic intent, never a color. */
const SOURCE_INTENT: Record<IntakeSource, Intent> = {
  file: "info",
  text: "neutral",
  url: "info",
  cli: "neutral",
  mcp: "neutral",
  ui: "neutral",
};

/** "auto" | the deciding actor | em-dash — the human-readable "decided by". */
function decidedText(r: IntakeBatchLedgerRow): string {
  if (!r.decision) return "—";
  if (r.decision === "auto") return "auto";
  return r.decidedBy ?? r.decision;
}

/** "People · 92%" — kind + calibrated confidence, or an em-dash pre-classify. */
function kindConfidence(r: IntakeBatchLedgerRow): string {
  if (!r.kind) return "—";
  const label = r.kind.replace(/^./, (c) => c.toUpperCase());
  if (r.confidence == null) return label;
  return `${label} · ${Math.round(r.confidence * 100)}%`;
}

const COLUMNS: DataTableColumn<IntakeBatchLedgerRow>[] = [
  {
    key: "label",
    header: "Batch",
    render: (r) => (
      <span className="font-mono type-subtext text-foreground">{r.label ?? r.uid}</span>
    ),
  },
  {
    key: "source",
    header: "Source",
    render: (r) => <IntentBadge intent={SOURCE_INTENT[r.source]}>{r.source}</IntentBadge>,
  },
  {
    key: "status",
    header: "Status",
    render: (r) => <StatusBadge domain="intake" status={r.status} />,
  },
  { key: "kind", header: "Kind", render: (r) => kindConfidence(r) },
  {
    key: "rows",
    header: "Rows",
    render: (r) => `${r.recordsLanded.toLocaleString()} → ${r.recordsMaterialized.toLocaleString()}`,
  },
  { key: "decided", header: "Decided", render: (r) => decidedText(r) },
  { key: "created", header: "Created", render: (r) => formatRelativeTime(r.createdAt) },
];

export function IntakeBatchesCard() {
  const { data, isLoading } = useApi(intakeContract.list, {});

  if (isLoading && !data) {
    return (
      <Card className="p-6">
        <SectionTitle variant="plain" title="Intake" />
        <p className="mt-3 type-body text-muted-foreground">Loading intake ledger…</p>
      </Card>
    );
  }
  if (!data) return null;

  const { batches, stats } = data;

  return (
    <Card className="space-y-5 p-6">
      <SectionTitle
        variant="plain"
        title="Intake"
        count={batches.length}
        subtitle="Batches captured through file, CLI/MCP, or URL — what landed, and what materialized."
      />

      {/* Roll-up stat strip (whole ledger, not just the page). */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard title="Batches (7d)" value={stats.batches7d.toLocaleString()} />
        <StatCard title="Rows landed" value={stats.rowsLanded.toLocaleString()} />
        <StatCard title="Materialized" value={stats.rowsMaterialized.toLocaleString()} />
        <StatCard
          title="Needs attention"
          value={stats.needsAttention.toLocaleString()}
          description="Awaiting a decision or failed"
          leadingDot={stats.needsAttention > 0 ? "warning" : undefined}
        />
      </div>

      {/* Full-bleed table: -mx-6 cancels the card's px-6 so the table's own
          first:pl-6/last:pr-6 is the single 24px gutter and the band stretches
          edge-to-edge, matching the Objects inventory table. */}
      <div className="-mx-6">
        <DataTable
          wrapInCard={false}
          columns={COLUMNS}
          rows={batches}
          rowKey={(r) => r.uid}
          emptyState={{
            title: "No intake yet",
            description:
              "Import data three ways: drop a file on this page, run `excellent-mcp ingest <file|url>`, or paste a table into the dock. Every capture lands here.",
          }}
        />
      </div>
    </Card>
  );
}
